import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import Tooltip, { type TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { RHFCheckboxField } from '@openx/components-core/lib/CheckboxField/RHFCheckboxField.tsx';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection.tsx';
import { RHFTextField } from '@openx/components-core/lib/TextField/RHFTextField.tsx';
import { RIGHT_COLUMN_WIDTH } from '@openx/constants/form.ts';
import { InfoIcon } from '@phosphor-icons/react/Info';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { exportTypes, getExportTypesLabels, providerFields } from '../../constants.ts';
import type { ProviderFormState, ProvidersRouteParams } from '../../types.ts';

const StyledExportTypesTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip describeChild {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    minWidth: 450,
  },
});

export const ProviderDetailsSections = () => {
  const { providerId } = useParams<ProvidersRouteParams>();
  const { t } = useTranslation();
  const { setValue } = useFormContext<ProviderFormState>();

  const exportTypeLabels = getExportTypesLabels(t);
  const isEdit = !!providerId;

  const handleNameChange = (name: string) => {
    if (!isEdit) setValue(providerFields.ID, name.replace(/\s/g, '').toLowerCase());
    setValue(providerFields.NAME, name, { shouldDirty: true });
  };

  return (
    <FormSection divider title={t('Provider Details')} rightColumnSize={RIGHT_COLUMN_WIDTH}>
      <RHFTextField
        name={providerFields.ID}
        label={t('Provider ID')}
        placeholder={t('Enter Provider ID')}
        fullWidth
        dataTest="provider-id"
        disabled
      />

      <RHFTextField
        name={providerFields.NAME}
        label={t('Provider Name')}
        placeholder={t('Enter Provider Name')}
        fullWidth
        dataTest="provider-name"
        margin="dense"
        onChange={(value, event) => {
          event.preventDefault();
          handleNameChange(value);
        }}
      />
      <Typography
        variant="body1"
        component="div"
        color={'textSecondary'}
        sx={{
          ml: 1.5,
          mt: 1,
        }}
      >
        {t('Allowed Export Types')}

        <StyledExportTypesTooltip
          placement="right"
          title={
            <Typography variant="body2" component="div">
              <Trans i18nKey="provider_export_type_tooltip" components={{ b: <b />, br: <br /> }}>
                The export type determines how segments are converted into targetable bidstream identifiers:
                <br />
                <br />
                <b>Bidstream:</b> Uses onboarded identifiers only, without expansion. Available for direct segments
                containing supported identifiers, such as cookies or IFAs.
                <br />
                <br />
                <b>Bidstream Plus:</b> Uses a custom mapping table to convert segments into supported identifiers.
                Available only where mapping is configured.
                <br />
                <br />
                <b>Identity Graph:</b> Uses the OA Match Graph to expand segments across linked identifiers for maximum
                reach and cross-device targeting.
              </Trans>
            </Typography>
          }
        >
          <IconButton component="div" data-test="provider-tooltip">
            <InfoIcon />
          </IconButton>
        </StyledExportTypesTooltip>
      </Typography>

      {Object.values(exportTypes).map(exportType => (
        <RHFCheckboxField
          key={exportType}
          name={`${providerFields.ALLOWED_EXPORT_TYPES}.${exportType}`}
          label={exportTypeLabels[exportType]}
          fullWidth
          margin="dense"
          value={exportType}
          data-test={`allowed-export-type-${exportType}`}
        />
      ))}
    </FormSection>
  );
};
